import { Card, CardContent, Input, Textarea } from '@ibsys/kit';
import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFormCodeGeneration } from '@/shared/hooks';
import { AiInput, IconButton } from '@/shared/ui';
import { Icon } from '@/shared/ui';
import { AttributeFormValues } from './validation';

type Props = {
  index: number;
  disabled?: boolean;
  onRemove: () => void;
};

export const SelectValueItem: FC<Props> = ({ index, disabled, onRemove }) => {
  const { t } = useTranslation(['attributes', 'common']);
  const { control, getValues, setValue } = useFormContext<AttributeFormValues>();

  // Use code generation hook for this specific select value item
  const { isCodeLocked, isGeneratingCode, handleNameBlur } = useFormCodeGeneration({
    getName: () => getValues(`selectValues.${index}.name`) ?? '',
    getCode: () => getValues(`selectValues.${index}.code`) ?? '',
    setCode: (code) => setValue(`selectValues.${index}.code`, code),
    entityType: 'selectValue',
    isEdit: false, // Always creating new select values
  });

  return (
    <Card className="p-2">
      <CardContent className={'p-2'}>
        <div className="flex justify-end mb-1">
          {!disabled && (
            <IconButton
              icon={<Icon type={'trash'} />}
              color={'destructive'}
              label={t('attributes:deleteValue')}
              onClick={onRemove}
            />
          )}
        </div>
        <div className={'grid grid-cols-1 sm:grid-cols-2 gap-2'}>
          <AiInput
            control={control}
            disabled={disabled}
            name={`selectValues.${index}.name`}
            label={t('attributes:name')}
            required
            isProcessing={isGeneratingCode}
            onBlur={handleNameBlur}
          />
          <Input
            control={control}
            disabled={disabled || isCodeLocked}
            name={`selectValues.${index}.code`}
            label={t('attributes:code')}
            required
            placeholder={isCodeLocked ? t('common:nameIsTheFirst') : undefined}
          />
          <Textarea
            className="sm:col-span-2"
            control={control}
            disabled={disabled}
            name={`selectValues.${index}.description`}
            label={t('attributes:description')}
            required
            tabIndex={1}
          />
        </div>
      </CardContent>
    </Card>
  );
};
